import { LANGUAGE_CONFIG, type SupportedLanguage } from "../config/languages";

type ResolveLanguageParams = {
  /** 1. バックエンドサーバのセッションロケール (最優先) */
  sessionLocale: string | null | undefined;
  /** 2. AuthStoreの設定（ユーザーが明示的に選択した言語） */
  clientLocale: SupportedLanguage | null | undefined;
  /** 3. ブラウザの言語設定 (最後の候補) */
  browserLanguage: string;
  supportedLanguages: typeof LANGUAGE_CONFIG;
};

type ValidateLanguageParams = {
  locale: string | null | undefined;
  supportedLanguages: typeof LANGUAGE_CONFIG;
};

export class LanguageResolver {
  /**
   * サポート対象の言語かどうか判定
   */
  static isSupported({
    locale,
    supportedLanguages,
  }: ValidateLanguageParams): SupportedLanguage | null {
    if (!locale) return null;

    // 完全一致のチェック
    if (locale in supportedLanguages) {
      return locale as SupportedLanguage;
    }

    // shortCodeとの一致をチェック
    const matchedLanguage = Object.values(supportedLanguages).find(
      (lang) => lang.shortCode === locale,
    );

    return matchedLanguage ? matchedLanguage.code : null;
  }

  /**
   * 言語設定の優先順位に基づいて使用する言語を決定
   *
   * 優先順位（README.md準拠）:
   * 1. バックエンドサーバのセッションロケール
   * 2. AuthStoreの設定（ユーザーが明示的に選択した言語）
   * 3. ブラウザの言語設定
   */
  static resolve({
    sessionLocale,
    clientLocale,
    browserLanguage,
    supportedLanguages,
  }: ResolveLanguageParams): SupportedLanguage {
    const languageChain = [sessionLocale, clientLocale, browserLanguage];

    for (const lang of languageChain) {
      const validLocale = this.isSupported({
        locale: lang,
        supportedLanguages,
      });
      if (validLocale) return validLocale;
    }

    return LANGUAGE_CONFIG["en-US"].code;
  }
}
